import React, { useEffect, useState } from 'react';
import ProductCard from './ProductCard';
import { useDarkMode } from '../contexts/DarkModeContext';
import { Product } from '../data/interfaces/Product';
import ShowMoreButton from './ShowMoreButton';
import { Variation } from '../data/interfaces/Variation';
import addonDataRaw from '../data/addonData.json';
import { Addon } from '../data/interfaces/Addon';

const addonData = addonDataRaw as Addon[];

interface AddonGroupProps {
    product: Product;
    variation: Variation;
}

const AddonGroup: React.FC<AddonGroupProps> = ({ product, variation }) => {
    const [groupedAddons, setGroupedAddons] = useState<{ [key: string]: Addon[] }>({});
    const [allAddons, setAllAddons] = useState<Addon[]>([]);
    const [showAll, setShowAll] = useState(false);
    const [pagination, setPagination] = useState<{ [key: string]: number }>({});
    const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 992);
    const { isDarkMode } = useDarkMode();

    const mediaPerPage = 8;
    const initialShowing = 4;

    useEffect(() => {
        const filteredAddons = addonData.filter(
            (addon) =>
                addon.releaseId === product.id &&
                addon.variationId === variation.variationId
        );

        const grouped = filteredAddons.reduce((acc, addon) => {
            const group = addon.group || 'Ungrouped';
            if (!acc[group]) acc[group] = [];
            acc[group].push(addon);
            return acc;
        }, {} as { [key: string]: Addon[] });

        setGroupedAddons(grouped);
        setAllAddons(filteredAddons);
    }, [product.id, variation.variationId]);


    useEffect(() => {
        const handleResize = () => {
            setIsLargeScreen(window.innerWidth >= 992);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleShowAll = () => setShowAll(prev => !prev);

    if (allAddons.length === 0) return null;

    const hasSegmentedAddons = Object.keys(groupedAddons).length > 1;

    return (
        <div className="mt-5">
            <h1 className="kanit-extrabold">Add-ons</h1>
            <div className={`card ${isDarkMode ? 'dark-mode-3' : 'light-mode-3'} p-3`}>
                {showAll ? (
                    <>
                        {Object.entries(groupedAddons).map(([group, addons], index) => {
                            const currentPage = pagination[group] || 1;
                            const indexOfLastAddon = currentPage * mediaPerPage;
                            const indexOfFirstAddon = indexOfLastAddon - mediaPerPage;
                            const currentAddonsDisplayed = addons.slice(indexOfFirstAddon, indexOfLastAddon);
                            const totalPages = Math.ceil(addons.length / mediaPerPage);

                            const handleNextPage = () => {
                                if (currentPage < totalPages) {
                                    setPagination(prev => ({ ...prev, [group]: currentPage + 1 }));
                                }
                            };

                            const handlePrevPage = () => {
                                if (currentPage > 1) {
                                    setPagination(prev => ({ ...prev, [group]: currentPage - 1 }));
                                }
                            };

                            const handlePageClick = (pageNumber: number) => {
                                setPagination(prev => ({ ...prev, [group]: pageNumber }));
                            };

                            return (
                                <div key={group}>
                                    <h3 className={index === 0 ? '' : 'mt-4'}>
                                        {group === 'Ungrouped' ? 'Miscellaneous' : group}
                                    </h3>
                                    <div
                                        className={`
                                            card row release-info-container
                                            ${hasSegmentedAddons ?
                                                `${isDarkMode ? 'dark-mode-1' : 'light-mode-2'}` :
                                                `${isDarkMode ? 'dark-mode-3' : 'light-mode-3'}`}
                                        `}
                                    >
                                        {currentAddonsDisplayed.map(addon => (
                                            <ProductCard
                                                key={addon.addonId}
                                                id={addon.addonId}
                                                title={addon.title}
                                                slug={addon.slug}
                                                description=""
                                                thumbnailUrl={addon.thumbnailUrl}
                                                version={addon.version}
                                                datePosted={addon.datePosted}
                                                dateUpdated={addon.dateUpdated}
                                                game={addon.game}
                                                tags={[]}
                                                isAddonCard={true}
                                                linkedProduct={product}
                                                linkedVariation={variation}
                                            />
                                        ))}

                                        {/* Pagination Controls */}
                                        {totalPages > 1 && (
                                            <nav aria-label={`Page navigation for ${group}`} className="mt-4">
                                                <ul className="pagination justify-content-center">
                                                    {/* Previous Button */}
                                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                        <a className="page-link" onClick={handlePrevPage} aria-label="Previous">
                                                            <span aria-hidden="true">&laquo;</span>
                                                        </a>
                                                    </li>

                                                    {/* Page Numbers */}
                                                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                                                        .filter(pageNumber => {
                                                            if (isLargeScreen) {
                                                                return true; // Show all pages on large screens
                                                            }
                                                            const maxDisplayedPages = 3;
                                                            const halfDisplayed = Math.floor(maxDisplayedPages / 2);
                                                            return (
                                                                pageNumber === 1 ||
                                                                pageNumber === totalPages ||
                                                                (pageNumber >= currentPage - halfDisplayed &&
                                                                    pageNumber <= currentPage + halfDisplayed)
                                                            );
                                                        })
                                                        .map((pageNumber, index, filteredPages) => (
                                                            <React.Fragment key={pageNumber}>
                                                                {index > 0 && pageNumber > filteredPages[index - 1] + 1 && (
                                                                    <li className="page-item disabled">
                                                                        <span className="page-link">...</span>
                                                                    </li>
                                                                )}
                                                                <li className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                                                                    <a
                                                                        className="page-link"
                                                                        onClick={() => handlePageClick(pageNumber)}
                                                                    >
                                                                        {pageNumber}
                                                                    </a>
                                                                </li>
                                                            </React.Fragment>
                                                        ))}

                                                    {/* Next Button */}
                                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                        <a className="page-link" onClick={handleNextPage} aria-label="Next">
                                                            <span aria-hidden="true">&raquo;</span>
                                                        </a>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </div>

                                    
                                </div>
                            );
                        })}
                    </>
                ) : (
                    <div className="row">
                        {allAddons.slice(0, initialShowing).map(addon => (
                            <ProductCard
                                key={addon.addonId}
                                id={addon.addonId}
                                title={addon.title}
                                slug={addon.slug}
                                description=""
                                thumbnailUrl={addon.thumbnailUrl}
                                version={addon.version}
                                datePosted={addon.datePosted}
                                dateUpdated={addon.dateUpdated}
                                game={addon.game}
                                tags={[]}
                                isAddonCard={true}
                                linkedProduct={product}
                                linkedVariation={variation}
                            />
                        ))}
                    </div>
                )}
                {allAddons.length > initialShowing && (
                    <ShowMoreButton showAll={showAll} toggleShowAll={toggleShowAll} />
                )}
            </div>
        </div>
    );
};

export default AddonGroup;